import React, { useState, useEffect, useMemo } from 'react';
import { attributeService, attributeValueService } from '../services';
import { DataTable } from '../components';
import {
  useGlobalState,
  useModalDialog,
  useMessage,
  usePrincipal,
  useSideDialog,
  useStatus,
} from '../hooks';
import {
  Asset,
  AssetName,
  AttributeSensitivity,
  canHaveAttributeValues,
  getObjectNameFromSelection,
  handleError,
  Role,
  resolveGlossaryAssetSubmit,
  fetchAttributeValues,
  searchAttributeValues,
} from '../helpers';

/**
 * Which attribute value column each of this list's filterable columns stands for, so a server-side
 * search of the values can translate one tick in the column selector. The description is left out
 * because it is stored as rich text, where a substring match would be matching markup as much as
 * words; the role, unit and sensitivity a value carries are aliased or blank rather than its own.
 */
const VALUE_COLUMN_FOR_LIST_COLUMN = {
  attribute_name: 'attribute_value_name',
  attribute_value_code: 'attribute_value_code',
};

export const AttributeList = () => {
  // The attributes. Their values are read when one is expanded, and held separately so that
  // reloading the list — which every save does — does not empty the open rows.
  const [items, setItems] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  const [enableEdit, setEnableEdit] = useState(true);
  const [enableEditText, setEnableEditText] = useState('');

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  // The attributes, plus the values of the ones that have been opened. The values are rows of the
  // same table, placed under their attribute by parent_unique_id.
  const rows = useMemo(() => [...items, ...attributeValues], [items, attributeValues]);

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { appendStatus } = useStatus({ setState: setItems, assetCategory: Asset.Glossary });

  useEffect(() => {
    fetchItems();
    clearSelectedItems(); // see comment in Glossary.js
  }, []);

  // TODO: Improve this to let users edit and delete assets of different types.
  // Current solution is to not allow edit and delete if assets of different types are selected.
  useEffect(() => {
    if (selectedItems.length > 0) {
      let tmpEnableEdit = true;
      let objectName = selectedItems[0]?.object_name;

      // Don't allow batch editing of items of different types (i.e. Attribute AND Attribute Value)
      for (let i = 0; i < selectedItems.length; i++) {
        if (objectName !== selectedItems[i].object_name) {
          tmpEnableEdit = false;
          setEnableEditText(
            'Assets of different types have been selected. Make sure to only select assets of the same type.'
          );
          break;
        }
      }

      // Don't allow batch editing of Attribute Values if there only is one Attribute
      if (selectedItems.filter(({ object_name }) => object_name === Asset.Attribute).length === 0) {
        if (
          items.filter(item => item.object_name === Asset.Attribute && canHaveAttributeValues(item))
            .length === 1
        ) {
          tmpEnableEdit = false;
          setEnableEditText(
            'Batch edit of attribute values requires at least two attributes of role Dimension'
          );
        }
      }

      setEnableEdit(tmpEnableEdit);
    } else {
      setEnableEdit(true);
    }
  }, [selectedItems]);

  const handleAssetByMethod = (method, items) => {
    items = items || selectedItems;
    const objectName = getObjectNameFromSelection(items, Asset.Attribute) || Asset.Attribute;
    switch (objectName) {
      case Asset.Attribute:
        if (method === 'add') {
          showSideDialog('addAttribute', items, data => actOnAsset('add', data, Asset.Attribute));
        } else if (method === 'add_child') {
          showSideDialog(
            'addAttributeValue',
            [
              {
                status_id: statuses.default_glossary_status_id,
                attribute_id: items[0].attribute_id,
              },
            ],
            data => actOnAsset('add', data, Asset.AttributeValue)
          );
        } else if (method === 'edit') {
          showSideDialog('editAttribute', items, data => actOnAsset('edit', data, Asset.Attribute));
        } else if (method === 'delete') {
          showModalDialog('deleteAttribute', items, data =>
            actOnAsset('delete', data, Asset.Attribute)
          );
        }
        break;

      case Asset.AttributeValue:
        if (method === 'add') {
          showSideDialog('addAttributeValue', items, data =>
            actOnAsset('add', data, Asset.AttributeValue)
          );
        } else if (method === 'edit') {
          showSideDialog('editAttributeValue', items, data =>
            actOnAsset('edit', data, Asset.AttributeValue)
          );
        } else if (method === 'delete') {
          showModalDialog('deleteAttributeValue', items, data =>
            actOnAsset('delete', data, Asset.AttributeValue)
          );
        }
        break;

      case 'mixed':
        // Not supported, do nothing

        break;
      default:
      //Nothing
    }
  };

  /**
   * Shapes an attribute value as a row of this list, aliased onto the attribute_* display keys its
   * columns read. The glossary trees do the same onto their own keys — same identity, same icon,
   * different column set.
   *
   * @param {object} value - Row from GET /attribute_value/all
   * @returns {object}
   */
  const toValueRow = value => ({
    ...value,
    object_id: value.attribute_value_id,
    object_name: Asset.AttributeValue,
    unique_id: `${Asset.AttributeValue}-${value.attribute_value_id}`,
    parent_unique_id: `${Asset.Attribute}-${value.attribute_id}`,
    attribute_name: value.attribute_value_name,
    attribute_description: value.attribute_value_description,
    // Its own kind, which draws its own icon — inheriting its attribute's role would draw a
    // Dimension icon on something that is not one.
    attribute_role_name: AssetName[Asset.AttributeValue],
    attribute_unit_code: undefined,
    pii: undefined,
    attribute_sensitivity_name: undefined,
    // Read from the view as its attribute's, and shown by the attribute one row above it. Repeating
    // it on every value would read as the value belonging to a glossary in its own right.
    glossary_name: null,
    link: `/attributes/${value.attribute_id}/${value.attribute_value_id}`,
  });

  /**
   * Replaces the loaded values of the given attributes, leaving every other attribute's alone.
   * @param {Array<number|string>|number|string} attributeIds - The attributes just read
   * @param {object[]} values - Their values as just read, in one list
   */
  const mergeValues = (attributeIds, values) => {
    // A set of what is being replaced, so re-reading many attributes is one pass over the state and
    // one render rather than one of each per attribute.
    const ids = new Set([].concat(attributeIds).map(Number));

    setAttributeValues(prev => [
      ...prev.filter(value => !ids.has(Number(value.attribute_id))),
      ...values,
    ]);
    appendStatus({ setState: setAttributeValues, assetCategory: Asset.Glossary });
  };

  /**
   * Loads the values of one attribute, for expanding its row. The promise is returned so the table
   * can show the row as loading until they arrive.
   * @param {object} item - The row being expanded
   * @returns {Promise<void>|undefined} undefined for a row that has no values to load
   */
  const fetchValues = item => {
    if (item.object_name !== Asset.Attribute) return;

    // Already read once. The rows are still here — collapsing a row does not discard them — so
    // re-expanding shows them again without another request. A change made here re-reads them.
    if (attributeValues.some(value => Number(value.attribute_id) === Number(item.attribute_id))) {
      return;
    }

    return fetchAttributeValues({ attributeIds: [item.attribute_id], toRow: toValueRow })
      .then(values => mergeValues(item.attribute_id, values))
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Re-reads the values of the attributes whose rows are open, after one of them has changed.
   *
   * All of them are read in one request: opening a row keeps its values until the page is left, so
   * what this covers grows as the list is explored, and a request each would be a request per row
   * ever opened.
   *
   * @param {Array<number|string>} [attributeIds] - Attributes to re-read; defaults to every open one
   */
  const refreshValues = attributeIds => {
    const ids = attributeIds || [...new Set(attributeValues.map(value => value.attribute_id))];

    if (ids.length === 0) return Promise.resolve();

    return fetchAttributeValues({ attributeIds: ids, toRow: toValueRow })
      .then(values => mergeValues(ids, values))
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Searches attribute values across every glossary, for the filter box. The list is not scoped to
   * one glossary, so neither is the search.
   * @param {string} term - What the user typed
   * @param {{signal: AbortSignal, top: number, fields: string[]}} options - From the DataTable
   * @returns {Promise<object[]>} Matching value rows
   */
  const searchValues = (term, { signal, top, fields }) =>
    searchAttributeValues({
      // Every value, bounded by $top: an attribute value belongs to exactly one attribute, and this
      // list holds them all.
      scope: 'attribute_value_id gt 0',
      term,
      fields,
      columnMap: VALUE_COLUMN_FOR_LIST_COLUMN,
      toRow: toValueRow,
      top,
      signal,
    }).catch(err => {
      showMessage(err, 'error');
      return [];
    });

  const fetchItems = async () => {
    const promise = new Promise(resolve => {
      attributeService
        .getAll()
        .then(res => {
          const result = res.attributes.map(x => ({
            ...x,
            object_id: x.attribute_id,
            object_name: Asset.Attribute,
            unique_id: `${Asset.Attribute}-${x.attribute_id}`, // important: prefix with "attribute" to assure unique ids
            parent_unique_id: null, // root description, set to NULL
            pii: x.is_pii ? 'PII' : null,
            pii_tooltip: x.is_pii
              ? 'Classified as Personally Identifiable Information (PII)'
              : null,
            link: `/attributes/${x.attribute_id}`,
            glossary_link: x.glossary_id ? `/glossaries/${x.glossary_id}` : null,
            className: 'bold',
          }));

          setItems(result);
          appendStatus();
          setLoading(false);
          fetchPrincipals(result, setItems);
          resolve(result);
        })
        .catch(err => {
          showMessage(err, 'error');
          setLoading(false);
        });
    });

    Promise.all([promise, fetchFieldCount(), fetchValueCount()])
      .then(result => {
        updateFieldCount(result[0], result[1], result[2]);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * The number of values each attribute has, which is what decides whether it offers to open. The
   * values themselves are read one attribute at a time, when a row is expanded.
   * @returns {Promise<Array<{attribute_id: number, attribute_value_count: number}>>}
   */
  const fetchValueCount = async () =>
    attributeService
      .getAttributeValueCount()
      .then(res => res?.attribute_value_count || [])
      .catch(() => []);

  const fetchFieldCount = async () => {
    return new Promise((resolve, reject) => {
      attributeService
        .getFieldCount()
        .then(res => {
          resolve(res.field_count);
        })
        .catch(err => {
          reject(err);
        });
    });
  };

  const updateFieldCount = (data, statistics, valueCounts) => {
    let statistic = {};
    let maxValue = 0;

    const valueCount = new Map(
      (valueCounts || []).map(row => [row.attribute_id, row.attribute_value_count])
    );

    statistics.forEach(row => {
      if (row.field_count > maxValue) {
        maxValue = row.field_count;
      }
    });

    setItems(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.attribute_id === item.attribute_id);

        return {
          ...item,
          associated_field_count: statistic ? statistic.field_count : 0,
          associated_field_count_max: maxValue,
          // How many values the attribute has, which is what tells the table it has any before
          // they are read.
          attribute_value_count: valueCount.get(item.attribute_id) || 0,
        };
      })
    );
  };

  const actOnAsset = async (action, data, objectName) => {
    data = Array.isArray(data) ? data : [data];

    let service;
    let isCreate = action === 'add';

    switch (objectName) {
      case Asset.Attribute: {
        // The form's role picker can make this a Term instead, which lands in the entity table, so
        // both the resource and whether it is a create are resolved from the submitted row.
        const resolved = resolveGlossaryAssetSubmit(data, action === 'add' ? 'add' : 'edit');
        service = resolved.service;
        data = resolved.payload;
        isCreate = resolved.isCreate;
        break;
      }
      case Asset.AttributeValue:
        service = attributeValueService;
        data = { attribute_values: data };
        break;
      default:
      // do nothing
    }

    try {
      const verb = action === 'delete' ? 'destroy' : isCreate ? 'create' : 'update';
      const res = await service[verb](data).catch(err => {
        throw err;
      });

      if (action === 'add') setSideDialog(prev => ({ ...prev, formErrors: {} }));
      if (action === 'edit') hideSideDialog();
      if (action === 'delete') hideModalDialog();
      await fetchItems();
      // The list carries the value counts; the values themselves are held per attribute, so the ones
      // on screen are re-read too. An edit can move a value to another attribute, hence both the
      // attributes that are open and any the save named.
      // An attribute save is included: its values are edited in the same dialog and travel with it.
      if (objectName === Asset.AttributeValue || objectName === Asset.Attribute) {
        const named = [
          ...(data.attribute_values || []).map(row => row.attribute_id),
          ...(data.attributes || []).map(row => row.attribute_id),
        ].filter(Boolean);
        refreshValues([
          ...new Set([...attributeValues.map(value => value.attribute_id), ...named]),
        ]);
      }
      clearSelectedItems();
      showMessage(res.message);
    } catch (err) {
      handleError({ err, showMessage, setSideDialog });
      throw err;
    }
  };

  /**
   * The "Add Child" button, which adds an attribute value to the one selected attribute.
   *
   * Only a Dimension can hold values, so for a Measure the button is shown but disabled with the
   * reason in its tooltip rather than hidden. The button pre-selects the attribute in the dialog,
   * which is what makes the check necessary here: the dialog's own attribute picker only offers
   * Dimensions, but a pre-selected one never passes through it.
   *
   * @returns {object} A DataTable button dimension, or `{}` when the selection is not a single attribute.
   */
  const getAddChildButton = () => {
    if (selectedItems.length === 1 && selectedItems[0].object_name === Asset.Attribute) {
      const allowed = canHaveAttributeValues(selectedItems[0]);
      return {
        label: 'Add Child',
        disabled: !allowed,
        tooltip: allowed
          ? 'Add an attribute value to the currently selected attribute'
          : "Attribute Values can only be registered on Attributes with role 'Dimension'",
        onClick: () => handleAssetByMethod('add_child', selectedItems),
      };
    }
    return {};
  };

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  // Rendered as a tab of the Glossaries page, so no page wrapper of its own — see views/Glossaries.js.
  return (
    <DataTable
      columns={[
        {
          id: 'attribute_name',
          name: 'Name',
          className: ':className',
          link: ':link',
          tooltip: 'attribute_description',
          inline: [{ type: 'icon', icon: 'attribute_role_name' }],
        },
        {
          id: 'attribute_unit_code',
          name: 'Attribute Unit',
          tooltip: 'attribute_unit_name',
        },
        {
          id: 'attribute_description',
          name: 'Description',
          type: 'description',
          viewMode: 'descriptions',
        },
        {
          id: 'associated_field_count',
          name: 'Associated Fields',
          type: 'chart',
          // A value has no fields of its own, so the cell is left blank rather than reading "-",
          // which would say the number is unknown.
          appliesTo: Asset.Attribute,
          chartType: 'bar',
          data: {
            data: 'associated_field_count',
            max: 'associated_field_count_max',
          },
          viewMode: 'statistics',
        },
        {
          // An attribute now belongs to a glossary, so a list of them all can say which one.
          id: 'glossary_name',
          name: 'Glossary',
          link: ':glossary_link',
        },
        {
          id: 'pii',
          name: '',
          type: 'icon',
          tooltip: 'pii_tooltip',
          className: 'no-right-margin',
        },
        {
          id: 'attribute_sensitivity_name',
          name: 'Data Sensitivity',
          type: 'pill',
          value: 'attribute_sensitivity_level',
          values: AttributeSensitivity,
          tooltip: 'attribute_sensitivity_name',
          className: 'bold',
        },
        ...(settings.SHOW_GLOSSARY_STATUSES
          ? [
              {
                id: 'status_name',
                name: 'Status',
                type: 'pill',
                value: 'status_id',
                values: statuses,
                tooltip: 'status_description',
              },
            ]
          : []),
        {
          id: 'principal_name',
          name: 'Owner',
          type: 'owner',
          // An owner belongs to the asset itself: a value inherits its parent's, and showing it
          // again on every row would read as the value having an owner of its own.
          appliesTo: Asset.Attribute,
          link: '/:principal_url',
          data: { photo: 'principal_photo' },
          tooltipSubtitle: 'principal_type_name',
        },
      ]}
      buttons={[
        {
          label: '+ Add',
          action: 'add',
          mainButton: true,
          onClick: items => handleAssetByMethod('add', items),
        },
        getAddChildButton(),
        {
          label: 'Edit',
          action: 'edit',
          disabled: !enableEdit,
          tooltip: !enableEdit && enableEditText,
          onClick: items => handleAssetByMethod('edit', items),
        },
        {
          label: 'Delete',
          action: 'delete',
          disabled: !enableEdit,
          tooltip:
            !enableEdit &&
            'Assets of different types have been selected. Make sure to only select assets of the same type.',
          onClick: items => handleAssetByMethod('delete', items),
        },
      ]}
      data={rows}
      idColumn="unique_id"
      parentIdColumn="parent_unique_id"
      primaryColumn="attribute_name"
      selectedItems={selectedItems}
      setSelectedItems={setSelectedItems}
      loading={loading}
      editable={editable}
      filterable={true}
      filterObjects={[
        'attribute_name',
        'attribute_description',
        'attribute_role_name',
        // An attribute value's code. Only values have one and no column shows it, but it is what
        // people search for, so it stays in the column selector.
        'attribute_value_code',
        'attribute_sensitivity_name',
        'is_pii',
        ...(settings.SHOW_GLOSSARY_STATUSES ? ['status_name'] : []),
        'principal_name',
      ]}
      showViewModes={true}
      expandable={true}
      expandableIndentIcons={true}
      // An attribute's values are read when it is expanded, so the count is what tells the table an
      // attribute has any before they exist.
      childCountColumn="attribute_value_count"
      onExpand={fetchValues}
      childFilter={{ fetch: searchValues }}
    />
  );
};
